/**
 * AnyModuleGuard - Proteção de rota para telas COMPARTILHADAS entre módulos
 *
 * Diferente do CrmModuleGuard (que exige um único módulo), este guard libera
 * o acesso quando o tenant possui AO MENOS UM dos módulos listados em
 * `anyOf`. Caso nenhum esteja habilitado, registra tentativa de bypass na
 * tabela security_bypass_attempts e redireciona para /tenant.
 *
 * Uso atual: rota /tenant/crm/customers (Clientes), utilizada por CRM,
 * Financeiro, Retenção, Jurídico e Veículos.
 *
 * CHANGELOG:
 *   RELEASE: 2026-09-17 v1.0.0 - Criação do guard multi-módulo (regra OR).
 *
 * @version 1.0.0
 */

import { ReactNode, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger para auditoria de segurança do guard multi-módulo
const guardLogger = createLogger('AnyModuleGuard');

/** Flags de módulo do tenant suportadas por este guard. */
export type TenantModuleFlag =
  | 'crm_enabled'
  | 'financial_enabled'
  | 'retention_enabled'
  | 'legal_enabled'
  | 'vehicles_enabled';

interface AnyModuleGuardProps {
  /** Lista de flags: basta UMA ativa para liberar o acesso (regra OR). */
  anyOf: TenantModuleFlag[];
  /** Conteúdo a ser renderizado quando ao menos um módulo está habilitado. */
  children: ReactNode;
}

/**
 * Registra tentativa de acesso à tela sem nenhum dos módulos exigidos
 * na tabela security_bypass_attempts para auditoria
 */
const logBypassAttempt = async (
  userId: string | undefined,
  tenantId: string | undefined,
  route: string,
  requiredFlags: TenantModuleFlag[]
): Promise<void> => {
  try {
    if (!userId) return;

    // Buscar full_name para incluir no details (conforme padrão de segurança)
    let fullName: string | null = null;
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .maybeSingle();
      fullName = profileData?.full_name || null;
    } catch {
      // Não bloquear se falhar busca do perfil
    }

    const { error } = await supabase
      .from('security_bypass_attempts')
      .insert({
        user_id: userId,
        attempted_tenant_id: tenantId || null,
        function_name: 'AnyModuleGuard',
        action: 'shared_screen_access_denied',
        attempted_value: route,
        user_agent: navigator.userAgent,
        details: {
          route,
          full_name: fullName,
          reason: 'no_required_module_enabled',
          required_flags: requiredFlags,
          timestamp: new Date().toISOString(),
        },
      });

    if (error) {
      guardLogger.error('Falha ao registrar tentativa de bypass (multi-módulo)', {
        error: error.message,
      });
    } else {
      guardLogger.warn('Tentativa de acesso sem nenhum módulo exigido registrada', {
        route,
        tenantId,
      });
    }
  } catch (err) {
    guardLogger.error('Erro ao registrar bypass attempt (multi-módulo)', { err });
  }
};

/**
 * Componente guard que bloqueia acesso à rota quando o tenant atual
 * não possui NENHUM dos módulos listados em `anyOf` habilitado.
 */
export function AnyModuleGuard({ anyOf, children }: AnyModuleGuardProps) {
  const location = useLocation();
  const { user, currentTenant } = useAuth();

  // Ref para evitar múltiplos logs da mesma tentativa
  const hasLoggedBypass = useRef(false);

  // Regra OR: basta um módulo habilitado para liberar a tela compartilhada
  const anyEnabled = anyOf.some(
    (flag) => (currentTenant?.[flag] as boolean | undefined) ?? false
  );

  // Registrar tentativa de bypass quando nenhum módulo está habilitado
  useEffect(() => {
    if (!anyEnabled && !hasLoggedBypass.current && currentTenant) {
      hasLoggedBypass.current = true;
      logBypassAttempt(user?.id, currentTenant?.id, location.pathname, anyOf);
    }
  }, [anyEnabled, user?.id, currentTenant, location.pathname, anyOf]);

  // Nenhum módulo exigido habilitado: redirecionar para dashboard principal
  if (!anyEnabled) {
    guardLogger.warn('Acesso bloqueado: nenhum módulo exigido habilitado para o tenant', {
      tenantId: currentTenant?.id,
      route: location.pathname,
      requiredFlags: anyOf,
    });
    return <Navigate to="/tenant" replace />;
  }

  // Ao menos um módulo habilitado - renderizar conteúdo
  return <>{children}</>;
}
